import { useEffect, useState } from "react";
import { Check, X, AlertTriangle, Upload, RefreshCw, Layers, Sliders, FileText, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const STATUS_LABEL = {
  validated: "Validée", corrected: "Corrigée", pending: "En attente",
  quarantined: "Quarantaine", ignored: "Ignorée",
};
const STATUS_COLOR = {
  validated: "bg-emerald-100 text-emerald-800", corrected: "bg-emerald-100 text-emerald-800",
  pending: "bg-amber-100 text-amber-900", quarantined: "bg-red-100 text-red-800",
  ignored: "bg-stone-200 text-stone-600",
};

const Chip = ({ children, active, onClick, testid }) => (
  <button data-testid={testid} onClick={onClick}
    className={`px-3 py-1.5 rounded-full text-xs border transition ${active ? "bg-bordeaux-700 text-ivory border-bordeaux-700" : "border-black/15 text-anthracite hover:border-anthracite"}`}>
    {children}
  </button>
);

function PriceLines() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("");
  const [edit, setEdit] = useState(null);

  const load = async () => {
    const { data } = await api.get(`/study/admin/price-lines${status ? `?status=${status}` : ""}`);
    setData(data);
  };
  useEffect(() => { load(); }, [status]);

  const act = async (line, action, extra = {}) => {
    try {
      await api.patch(`/study/admin/price-lines/${line.id}`, { action, ...extra });
      toast.success("Ligne mise à jour");
      setEdit(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action refusée");
    }
  };

  if (!data) return <div className="text-white/60 text-sm">Chargement…</div>;

  return (
    <div data-testid="admin-price-lines">
      <div className="flex flex-wrap items-center gap-2 mb-5">
        <Chip active={!status} onClick={() => setStatus("")} testid="filter-all">Toutes ({data.total})</Chip>
        {Object.entries(data.counts).map(([k, v]) => (
          <Chip key={k} active={status === k} onClick={() => setStatus(k)} testid={`filter-${k}`}>
            {STATUS_LABEL[k] || k} ({v})
          </Chip>
        ))}
        <button onClick={load} className="ml-auto text-white/60 hover:text-ivory p-2"><RefreshCw size={15} /></button>
      </div>

      <div className="space-y-3">
        {data.lines.map((l) => (
          <div key={l.id} data-testid={`price-line-${l.id}`} className="bg-white/5 border border-white/10 rounded-rama-sm p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-ivory text-sm font-medium">{l.rawLabel}</div>
                <div className="text-xs text-white/50 mt-1">
                  {l.serviceCode || "— à normaliser —"} · {l.quantity ?? "?"} {l.normalizedUnit || "?"} ·
                  PU {l.displayedUnitPrice ?? "?"} € · montant {l.displayedAmount ?? "?"} €
                  {l.effectiveUnitPrice != null && l.effectiveUnitPrice !== l.displayedUnitPrice && (
                    <span className="text-amber-300"> · PU effectif {l.effectiveUnitPrice} €</span>
                  )}
                </div>
                <div className="text-xs text-white/40 mt-1">
                  {l.documentReference} ({l.documentType}) · cluster {l.projectClusterId} · {l.materialsMode} · {l.date || "?"}
                </div>
                {l.exclusionReason && (
                  <div className="text-xs text-amber-300 mt-2 flex items-start gap-1.5">
                    <AlertTriangle size={12} className="mt-0.5 shrink-0" /> {l.exclusionReason}
                  </div>
                )}
              </div>
              <div className="flex flex-col items-end gap-2">
                <span className={`text-[0.65rem] px-2 py-1 rounded-full ${STATUS_COLOR[l.validationStatus]}`}>
                  {STATUS_LABEL[l.validationStatus]}
                </span>
                <span className={`text-[0.65rem] ${l.engineEligible ? "text-emerald-300" : "text-white/40"}`}>
                  {l.engineEligible ? "utilisée par le moteur" : "hors moteur"}
                </span>
                {l.arithmeticVariance ? (
                  <span className="text-[0.65rem] text-red-300">écart {l.arithmeticVariance} €</span>
                ) : null}
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mt-3">
              <Chip testid={`validate-${l.id}`} onClick={() => act(l, "validate")}>Valider</Chip>
              <Chip testid={`correct-${l.id}`} onClick={() => setEdit(edit === l.id ? null : l.id)}>Corriger</Chip>
              <Chip onClick={() => act(l, "quarantine", { reason: "Quarantaine manuelle" })}>Quarantaine</Chip>
              <Chip onClick={() => act(l, "duplicate")}>Doublon</Chip>
              <Chip onClick={() => act(l, "supersede", { reason: "Version remplacée" })}>Version remplacée</Chip>
              <Chip onClick={() => act(l, "ignore")}>Ignorer</Chip>
              <Chip onClick={() => act(l, "request_check")}>Vérification</Chip>
            </div>

            {edit === l.id && (
              <form className="mt-4 flex flex-wrap items-end gap-3"
                onSubmit={(e) => {
                  e.preventDefault();
                  const f = new FormData(e.target);
                  act(l, "correct", {
                    quantity: parseFloat(f.get("quantity")),
                    unitPrice: parseFloat(f.get("unitPrice")),
                    materialsMode: f.get("materialsMode"),
                    reason: f.get("reason") || "Correction manuelle",
                  });
                }}>
                <label className="text-xs text-white/60">Quantité
                  <input name="quantity" defaultValue={l.quantity ?? ""} className="block w-24 bg-transparent border-b border-white/30 py-1 text-ivory" />
                </label>
                <label className="text-xs text-white/60">PU (€)
                  <input name="unitPrice" defaultValue={l.effectiveUnitPrice ?? l.displayedUnitPrice ?? ""} className="block w-24 bg-transparent border-b border-white/30 py-1 text-ivory" />
                </label>
                <label className="text-xs text-white/60">Fournitures
                  <select name="materialsMode" defaultValue={l.materialsMode} className="block bg-anthracite border-b border-white/30 py-1 text-ivory text-sm">
                    <option value="rama_supplies_all">RAMA fournit</option>
                    <option value="client_supplies_materials">Client fournit</option>
                    <option value="unknown">Inconnu</option>
                  </select>
                </label>
                <label className="text-xs text-white/60 flex-1 min-w-[160px]">Motif
                  <input name="reason" className="block w-full bg-transparent border-b border-white/30 py-1 text-ivory" />
                </label>
                <button type="submit" className="px-3 py-1.5 rounded-full bg-bordeaux-700 text-ivory text-xs">Enregistrer</button>
                <button type="button" onClick={() => setEdit(null)} className="text-white/50 p-1"><X size={14} /></button>
              </form>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function CatalogTab() {
  const [items, setItems] = useState([]);
  const load = async () => setItems((await api.get("/study/admin/catalog")).data);
  useEffect(() => { load(); }, []);
  const toggle = async (c, field) => {
    await api.patch(`/study/admin/catalog/${c.code}`, { [field]: !c[field] });
    toast.success(`${c.clientLabel} mis à jour`);
    load();
  };
  return (
    <div data-testid="admin-catalog" className="space-y-2">
      {items.map((c) => (
        <div key={c.code} className="bg-white/5 border border-white/10 rounded-rama-sm p-4 flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-ivory text-sm">{c.clientLabel} <span className="text-white/40">· {c.code}</span></div>
            <div className="text-xs text-white/50">{c.family} · unité {c.preferredUnit} · {c.scopeIncludes?.length || 0} inclusions · {c.scopeExcludes?.length || 0} exclusions</div>
          </div>
          <div className="flex items-center gap-2">
            {c.requiresHumanValidation && <span className="text-[0.65rem] text-amber-300 flex items-center gap-1"><ShieldAlert size={11} /> validation</span>}
            <Chip active={c.isActive} onClick={() => toggle(c, "isActive")} testid={`catalog-active-${c.code}`}>
              {c.isActive ? "Active" : "Inactive"}
            </Chip>
            <Chip active={c.isPublic} onClick={() => toggle(c, "isPublic")}>{c.isPublic ? "Publique" : "Interne"}</Chip>
          </div>
        </div>
      ))}
    </div>
  );
}

function ConfigTab() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get("/study/admin/config").then(({ data }) => setCfg(data)); }, []);
  if (!cfg) return <div className="text-white/60 text-sm">Chargement…</div>;
  const save = async (patch) => {
    const { data } = await api.put("/study/admin/config", patch);
    setCfg(data);
    toast.success("Configuration enregistrée (version " + data.priceBookVersion + ")");
  };
  return (
    <div data-testid="admin-study-config" className="space-y-6">
      <div className="bg-white/5 border border-white/10 rounded-rama-sm p-5">
        <div className="text-ivory font-display text-lg mb-4">Poids du score de similarité</div>
        <form className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            const f = new FormData(e.target);
            const weights = {};
            Object.keys(cfg.similarityWeights).forEach((k) => { weights[k] = parseFloat(f.get(k)); });
            save({ similarityWeights: weights });
          }}>
          {Object.entries(cfg.similarityWeights).map(([k, v]) => (
            <label key={k} className="text-xs text-white/60">{k}
              <input name={k} defaultValue={v} data-testid={`weight-${k}`} className="block w-full bg-transparent border-b border-white/30 py-1 text-ivory" />
            </label>
          ))}
          <button type="submit" className="self-end px-4 py-2 rounded-full bg-bordeaux-700 text-ivory text-xs">Enregistrer</button>
        </form>
      </div>

      <div className="bg-white/5 border border-white/10 rounded-rama-sm p-5">
        <div className="text-ivory font-display text-lg mb-2">Famille façade / ITE</div>
        <p className="text-xs text-white/50 mb-4">Désactivée : les références du devis 489/24 ne servent que d'ancre de chantier et ne sont jamais converties en €/m².</p>
        <Chip active={cfg.facadeFamilyActive} testid="toggle-facade"
          onClick={() => save({ facadeFamilyActive: !cfg.facadeFamilyActive })}>
          {cfg.facadeFamilyActive ? "Active dans l'estimateur" : "Inactive dans l'estimateur"}
        </Chip>
      </div>

      <div className="bg-white/5 border border-white/10 rounded-rama-sm p-5">
        <div className="text-ivory font-display text-lg mb-4">Revalorisation des prix anciens</div>
        <form className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            const f = new FormData(e.target);
            save({ revaluation: {
              enabled: f.get("enabled") === "on", index: f.get("index"),
              coefficient: parseFloat(f.get("coefficient")) || null, appliedFrom: f.get("appliedFrom"),
              validatedBy: f.get("validatedBy"), justification: f.get("justification") } });
          }}>
          <label className="text-xs text-white/60">Indice
            <input name="index" defaultValue={cfg.revaluation?.index || ""} className="block w-full bg-transparent border-b border-white/30 py-1 text-ivory" />
          </label>
          <label className="text-xs text-white/60">Coefficient
            <input name="coefficient" defaultValue={cfg.revaluation?.coefficient || ""} className="block w-full bg-transparent border-b border-white/30 py-1 text-ivory" />
          </label>
          <label className="text-xs text-white/60">Date d'effet
            <input name="appliedFrom" type="date" defaultValue={cfg.revaluation?.appliedFrom || ""} className="block w-full bg-transparent border-b border-white/30 py-1 text-ivory" />
          </label>
          <label className="text-xs text-white/60">Validé par
            <input name="validatedBy" defaultValue={cfg.revaluation?.validatedBy || ""} className="block w-full bg-transparent border-b border-white/30 py-1 text-ivory" />
          </label>
          <label className="text-xs text-white/60 lg:col-span-3">Justification
            <input name="justification" defaultValue={cfg.revaluation?.justification || ""} className="block w-full bg-transparent border-b border-white/30 py-1 text-ivory" />
          </label>
          <label className="text-xs text-white/60 flex items-center gap-2 mt-4">
            <input type="checkbox" name="enabled" defaultChecked={cfg.revaluation?.enabled} /> Activer
          </label>
          <button type="submit" className="self-end px-4 py-2 rounded-full bg-bordeaux-700 text-ivory text-xs">Enregistrer</button>
        </form>
      </div>
    </div>
  );
}

function DocumentsTab() {
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = async () => setDocs((await api.get("/study/admin/documents")).data);
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const b64 = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.onerror = rej;
        r.readAsDataURL(file);
      });
      const type = document.getElementById("doc-type").value;
      const { data } = await api.post("/study/admin/documents/import", {
        filename: file.name, content_b64: b64, document_type: type,
      });
      toast[data.duplicate ? "warning" : "success"](data.message);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import impossible");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  return (
    <div data-testid="admin-study-documents" className="space-y-5">
      <div className="bg-white/5 border border-white/10 rounded-rama-sm p-5">
        <div className="text-ivory font-display text-lg mb-3">Importer un document</div>
        <p className="text-xs text-white/50 mb-4">
          PDF, Excel ou CSV. Le fichier est haché (anti-doublon), les lignes extraites arrivent en file d'attente de
          validation : aucune ligne n'alimente le moteur avant validation humaine. Les situations d'avancement ne
          servent jamais de prix unitaire.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <select id="doc-type" defaultValue="devis" data-testid="doc-type" className="bg-anthracite border border-white/20 rounded-rama-sm px-3 py-2 text-sm text-ivory">
            <option value="devis">Devis</option>
            <option value="facture">Facture</option>
            <option value="facture_situation">Facture de situation</option>
            <option value="autre">Autre</option>
          </select>
          <label className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-bordeaux-700 text-ivory text-xs cursor-pointer">
            <Upload size={14} /> {busy ? "Import…" : "Choisir un fichier"}
            <input type="file" accept=".pdf,.xlsx,.xlsm,.csv" onChange={upload} className="hidden" data-testid="doc-upload" />
          </label>
        </div>
      </div>

      <div className="space-y-2">
        {docs.map((d) => (
          <div key={d.id} className="bg-white/5 border border-white/10 rounded-rama-sm p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-ivory text-sm">{d.reference} <span className="text-white/40">· {d.type}</span></div>
                <div className="text-xs text-white/50">{d.date} · cluster {d.project_cluster_id}
                  {d.is_duplicate && " · doublon"}{d.is_superseded && " · version remplacée"}</div>
                {d.note && <div className="text-xs text-white/40 mt-1">{d.note}</div>}
              </div>
              <span className={`text-[0.65rem] px-2 py-1 rounded-full ${d.is_pricing_eligible ? "bg-emerald-100 text-emerald-800" : "bg-stone-200 text-stone-600"}`}>
                {d.is_pricing_eligible ? "éligible pricing" : "hors pricing"}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StudiesTab() {
  const [rows, setRows] = useState([]);
  const [audit, setAudit] = useState(null);
  useEffect(() => { api.get("/study/admin/studies").then(({ data }) => setRows(data)); }, []);
  return (
    <div data-testid="admin-studies" className="space-y-3">
      {rows.length === 0 && <div className="text-white/50 text-sm">Aucune pré-étude enregistrée.</div>}
      {rows.map((r) => (
        <div key={r.id} className="bg-white/5 border border-white/10 rounded-rama-sm p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-ivory text-sm">{r.summary || "Pré-étude"}</div>
              <div className="text-xs text-white/50">{r.createdAt?.slice(0, 16).replace("T", " ")} · niveau {r.level || "—"}
                {r.central ? ` · ${Math.round(r.central).toLocaleString("fr-FR")} € HT` : ""}
                {r.requiresVisit ? " · visite recommandée" : ""}</div>
            </div>
            {r.auditTraceId && (
              <Chip testid={`audit-${r.id}`} onClick={async () => {
                const { data } = await api.get(`/study/admin/audit/${r.auditTraceId}`);
                setAudit(data);
              }}>Voir l'audit</Chip>
            )}
          </div>
        </div>
      ))}
      {audit && (
        <div className="bg-black/40 border border-white/10 rounded-rama-sm p-4" data-testid="audit-detail">
          <div className="flex items-center justify-between mb-3">
            <div className="text-ivory text-sm">Trace d'audit {audit.id?.slice(0, 8)} · niveau {audit.level}</div>
            <button onClick={() => setAudit(null)} className="text-white/50"><X size={14} /></button>
          </div>
          <pre className="text-[0.65rem] text-white/70 overflow-auto max-h-80">{JSON.stringify(audit, null, 1)}</pre>
        </div>
      )}
    </div>
  );
}

const SUBTABS = [
  { v: "lines", l: "Base tarifaire", icon: Layers },
  { v: "catalog", l: "Catalogue", icon: Check },
  { v: "config", l: "Règles & coefficients", icon: Sliders },
  { v: "documents", l: "Documents", icon: FileText },
  { v: "studies", l: "Pré-études & audit", icon: ShieldAlert },
];

export default function AdminEtude() {
  const [sub, setSub] = useState("lines");
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-6">
        {SUBTABS.map((t) => (
          <button key={t.v} data-testid={`etude-subtab-${t.v}`} onClick={() => setSub(t.v)}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-rama-sm text-sm transition ${sub === t.v ? "bg-bordeaux-700 text-ivory" : "text-white/70 hover:text-ivory hover:bg-white/5"}`}>
            <t.icon size={14} /> {t.l}
          </button>
        ))}
      </div>
      {sub === "lines" && <PriceLines />}
      {sub === "catalog" && <CatalogTab />}
      {sub === "config" && <ConfigTab />}
      {sub === "documents" && <DocumentsTab />}
      {sub === "studies" && <StudiesTab />}
    </div>
  );
}
